import type { ChangeEvent } from 'react';
import { useState } from 'react';

import { ExpandMore } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Accordion,
  AccordionDetails,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';

import Button from '@/components/Buttons/Button';
import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import Popper from '@/components/Popper';
import { useAppSelector } from '@/hooks/redux-hooks';
import AbsoluteSelector from '@/pages/Markets/MarketsComponents/Chart/ForwardCurveChartBlock/CurveComparisonSelector/AbsoluteSelector';
import { parseSelectedToIso } from '@/pages/Markets/MarketsComponents/Chart/ForwardCurveChartBlock/CurveComparisonSelector/parseRelativeDateSelectionToIso';
import RelativeSelector from '@/pages/Markets/MarketsComponents/Chart/ForwardCurveChartBlock/CurveComparisonSelector/RelativeSelector';

import { getLatestTransactionDate } from './util/getLatestTransactionDate';

const showDuplicatedErrorMessage = () => {
  enqueueSnackbar('This date is already selected!', {
    variant: 'error',
    autoHideDuration: 5000
  });
};

const CurveComparisonSelector = ({
  chartSeries,
  label,
  transactionDates,
  comparisonSelectorIndex,
  deleteFromTemplateSeriesWith,
  populateTemplateSeriesWith
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  label: string;
  transactionDates: string[];
  comparisonSelectorIndex: number;
  deleteFromTemplateSeriesWith: (comparisonSelectorIndex: number) => void;
  populateTemplateSeriesWith: (selectedDateString: string, comparisonSelectorIndex: number) => void;
}) => {
  const [compareTo, setCompareTo] = useState<string>('1');
  const [compareToUnit, setCompareToUnit] = useState<string>('d');
  const latestTransactionDate: number | undefined = getLatestTransactionDate(chartSeries);

  const transactionDate =
    transactionDates.length > 0 ? transactionDates[comparisonSelectorIndex] : null;
  const [comparisonDate, setComparisonDate] = useState<Date | null>(
    transactionDate ? dayjs(transactionDate).toDate() : null
  );
  const [comparisonType, setComparisonType] = useState<string>(
    comparisonDate ? 'absolute' : 'relative'
  );
  const { userConfig } = useAppSelector(state => state.userConfig);
  const dateFormat = userConfig.dateFormat.value.toUpperCase();

  const handleChangeRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setComparisonType(e.target.value);
  };
  const onAddComparison = () => {
    const selectedDateString = parseSelectedToIso(
      latestTransactionDate,
      comparisonDate,
      comparisonType,
      compareTo,
      compareToUnit
    );
    if (transactionDates.includes(selectedDateString)) {
      showDuplicatedErrorMessage();
      return;
    }

    populateTemplateSeriesWith(selectedDateString, comparisonSelectorIndex);
  };

  return (
    <Box>
      <Popper
        buttonContent={
          <Box>
            {transactionDate ? (
              <Box
                alignItems='center'
                display='flex'
                justifyContent='space-between'
                sx={{ width: 170 }}
              >
                <Typography variant='body2'>{dayjs(transactionDate).format(dateFormat)}</Typography>
                <CloseIcon
                  sx={{ fontSize: 16, ml: 1, mr: 0 }}
                  onClick={e => {
                    e.stopPropagation();
                    deleteFromTemplateSeriesWith(comparisonSelectorIndex);
                  }}
                />
              </Box>
            ) : (
              <Box
                alignItems='center'
                display='flex'
                sx={{ width: 170 }}
              >
                <Typography variant='body2'>{`${label} ${comparisonSelectorIndex + 1}`}</Typography>
                <ExpandMore sx={{ fontSize: 16, ml: 1, mr: 0 }} />
              </Box>
            )}
          </Box>
        }
        buttonVariant='outlined'
        modifiers={[
          {
            name: 'flip',
            enabled: false
          }
        ]}
        placement='bottom-start'
        sx={{ ml: 2 }}
      >
        <Box
          sx={{
            minWidth: 400,
            borderBottom: '1px solid',
            borderLeft: '1px solid',
            borderColor: 'primary.main',
            display: 'flex',
            flexDirection: 'column',
            fontSize: '14px'
          }}
        >
          <Accordion defaultExpanded>
            <AccordionDetails
              sx={{
                backgroundColor: 'secondary.main',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography sx={{ mb: 1.5, fontSize: 16, fontWeight: 700 }}>
                Date selection
              </Typography>
              <Typography sx={{ mb: 1.5 }}>Select a date to compare</Typography>
              <FormControl>
                <RadioGroup
                  value={comparisonType}
                  onChange={handleChangeRadio}
                >
                  <RelativeSelector
                    compareTo={compareTo}
                    compareToUnit={compareToUnit}
                    setCompareTo={setCompareTo}
                    setCompareToUnit={setCompareToUnit}
                    setComparisonType={setComparisonType}
                  />

                  <FormControlLabel
                    control={<Radio />}
                    label='Compare to date'
                    value='absolute'
                  />
                  <AbsoluteSelector
                    comparisonDate={comparisonDate}
                    comparisonType={comparisonType}
                    setComparisonDate={setComparisonDate}
                    startPoint={latestTransactionDate}
                  />
                </RadioGroup>
              </FormControl>
              <Button
                sx={{ color: 'primary.main', borderColor: 'primary.main', mx: 3, mt: 1 }}
                variant='outlined'
                onClick={onAddComparison}
              >
                Add
              </Button>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Popper>
    </Box>
  );
};
export default CurveComparisonSelector;
